import { Button, Stack, Typography, TypographyProps } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import PageTabsV2 from "@/App/Layouts/PageBody/PageTabsV2";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import AuthorizedElement from "@/common/components/Auth/AuthorizedElement";
import AuthorizedTenantElement from "@/common/components/Auth/AuthorizedTenantElement";
import AppIcon from "@/common/components/Icons/AppIcon";
import AppLink from "@/common/components/Link/AppLink";
import { AppModalCloseIconButton } from "@/common/components/Modals/AppModalCloseIconButton";
import { PersistenceKey } from "@/common/constants/persistenceKey";
import { ROUTE_PATH } from "@/common/constants/routing";
import { useBreadcrumbReplacements } from "@/common/contexts/breadcrumbs";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useCurrentTenant } from "@/common/hooks/entity/tenant/useCurrentTenant";
import { useCommonViewParams } from "@/common/hooks/layout/useCommonViewParams";
import { useRealtimeDataUpdates } from "@/common/hooks/realtime/useRealtimeDataUpdates";
import { useAppHistory } from "@/common/hooks/routing/useAppHistory";
import { DataUpdatesChannelName } from "@/common/realtime/dataUpdatesChannelName";
import { ViewVariant } from "@/common/ts/layout";
import { apiClient } from "@/core/api/ApiClient";
import {
  AppPermission,
  DataUpdatesHubClientMethodName,
  EntityType,
  VehicleOperationType,
  VisualInspectionDto,
} from "@/core/api/generated/v1";

import { VehicleOperationViewPageHeader } from "../../Vehicle/View/Header/VehicleOperationViewPageHeader";
import BaseEntityView, { BaseEntityViewInheritableProps } from "../../components/BaseEntityView";
import VisualInspectionMenu from "../VisualInspectionMenu";
import CommentsTabContent from "./Tabs/CommentsTabContent";
import ContractTabContent from "./Tabs/ContractTabContent";
import GeneralHistoryTabContent from "./Tabs/GeneralHistoryTabContent";
import OverviewTabContent from "./Tabs/OverviewTabContent";
import VehicleTabContent from "./Tabs/VehicleTabContent";

export enum VisualInspectionViewPageTabs {
  Overview = "Overview",
  Vehicle = "Vehicle",
  Contract = "Contract",
  GeneralHistory = "GeneralHistory",
  Comments = "Comments",
}

const defaultDisplayProps = {
  breadcrumbs: true,
  header: true,
  actions: true,
  /** For modal view.  */
  closeInHeader: true,
  viewVariant: ViewVariant.Page,
};
const defaultHeaderProps = {
  withLink: false,
  typographyProps: undefined as Partial<TypographyProps> | undefined,
};

export interface VisualInspectionViewProps extends BaseEntityViewInheritableProps<VisualInspectionDto> {
  visualInspectionId: string | null | undefined;
  visualInspection: VisualInspectionDto | null | undefined;
  displayProps?: Partial<typeof defaultDisplayProps>;
  headerProps?: Partial<typeof defaultHeaderProps>;
}

export default function VisualInspectionView({
  visualInspectionId,
  visualInspection,
  displayProps,
  headerProps,
  onUpdate,
  onDelete,
  onClose,
}: VisualInspectionViewProps) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };

  const history = useAppHistory();
  const currentTenant = useCurrentTenant();
  const commonViewParams = useCommonViewParams({
    statePersistence: {
      persistenceKey: PersistenceKey.forEntityView(EntityType.VisualInspection),
      viewVariant: displayProps?.viewVariant,
    },
  });

  const visualInspectionRequest = useApiRequest(
    apiClient.visualInspectionsApi.apiV1VisualInspectionsVisualInspectionIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      visualInspectionId: visualInspectionId!,
    },
    {
      deps: [visualInspectionId],
      skip: !visualInspectionId,
    },
  );
  visualInspection = visualInspection || visualInspectionRequest?.data;

  useRealtimeDataUpdates({
    channelNames: [
      DataUpdatesChannelName.Entity(
        currentTenant?.id,
        EntityType.VisualInspection,
        visualInspectionId || "",
      ),
    ],
    methodNames: [DataUpdatesHubClientMethodName.EntityChanged],
    handler: undefined,
    entityChangedHandler: (methodName, data) => {
      console.error(
        `VisualInspectionView: Realtime update received for ${methodName} with data:`,
        data,
      );
      visualInspectionRequest.handleEntityChanged(data);
    },
  });

  useBreadcrumbReplacements({
    waitTimeout: 10_000,
    idBreadcrumb: visualInspection && {
      idValue: visualInspection?.id || "",
      newTitle: visualInspection?.localNumber || "",
    },
  });

  return (
    <BaseEntityView
      entityType={EntityType.VisualInspection}
      entityId={visualInspectionId}
      entity={visualInspection}
      entityRequest={visualInspectionRequest}
    >
      <ViewLayout
        displayProps={displayProps}
        header={
          visualInspection?.vehicle && (
            <VehicleOperationViewPageHeader
              operationType={VehicleOperationType.VisualInspection}
              operationEntity={visualInspection}
              vehicleId={visualInspection?.vehicle?.id}
              vehicle={visualInspection?.vehicle || undefined}
              titleProps={{
                to: headerProps?.withLink
                  ? ROUTE_PATH.VISUAL_INSPECTION_VIEW(visualInspection?.id)
                  : undefined,
                typographyProps: headerProps?.typographyProps,
              }}
              primaryActions={
                displayProps.actions && (
                  <Stack direction='row' gap={1}>
                    {visualInspection?.contract && (
                      <Button
                        variant='outlined'
                        color='text'
                        sx={{ ml: "auto" }}
                        component={AppLink}
                        to={ROUTE_PATH.CONTRACT_VIEW(visualInspection?.contract.id)}
                      >
                        <AppIcon of='documents' />
                        <Typography mr={1} ml={1}>
                          Contract
                        </Typography>
                        <Typography>
                          {visualInspection?.contract?.externalNumber ||
                            visualInspection?.contract?.localNumber}
                        </Typography>
                      </Button>
                    )}

                    <AuthorizedTenantElement tenantIds={[visualInspection?.tenantId]}>
                      <AuthorizedElement permissions={[AppPermission.VisualInspectionManage]}>
                        <Button
                          variant='outlined'
                          color='text'
                          component={RouterLink}
                          to={ROUTE_PATH.VISUAL_INSPECTION_EDIT(visualInspection?.id)}
                          startIcon={<AppIcon of='edit' />}
                        >
                          Edit
                        </Button>
                      </AuthorizedElement>
                    </AuthorizedTenantElement>
                  </Stack>
                )
              }
              secondaryActions={
                visualInspection && (
                  <VisualInspectionMenu
                    entity={visualInspection}
                    displayProps={{
                      actions: {
                        downloadPdf: true,
                        edit: false,
                        delete: true,
                        addToContract: true,
                        addToAssignment: true,
                        smartAutoInspection: true,
                      },
                    }}
                    onUpdate={(newValue) => {
                      newValue
                        ? visualInspectionRequest.replaceData(newValue)
                        : visualInspectionRequest.refetch();
                      onUpdate?.(newValue);
                    }}
                    onDelete={() => (onDelete ? onDelete() : history.goBack())}
                  />
                )
              }
              closeAction={
                displayProps.closeInHeader && onClose ? (
                  <AppModalCloseIconButton onClick={onClose} />
                ) : undefined
              }
              onTagsUpdated={(newValue) => {
                visualInspectionRequest.updateData((x) => {
                  x.tags = newValue || undefined;
                  onUpdate?.(x);
                });
              }}
            />
          )
        }
      >
        <PageTabsV2
          tabIdsDefinition={VisualInspectionViewPageTabs}
          defaultTabId={VisualInspectionViewPageTabs.Overview}
          commonViewParams={commonViewParams}
          tabs={[
            { label: "Overview", value: VisualInspectionViewPageTabs.Overview, isHideable: false },
            {
              label: "Vehicle",
              value: VisualInspectionViewPageTabs.Vehicle,
              permissions: [AppPermission.VehicleRead],
            },
            ...(visualInspection?.contract?.id
              ? [
                  {
                    label: "Contract",
                    value: VisualInspectionViewPageTabs.Contract,
                    permissions: [AppPermission.ContractRead],
                  },
                ]
              : []),
            {
              label: "History",
              value: VisualInspectionViewPageTabs.GeneralHistory,
              permissions: [AppPermission.GeneralHistoryRead],
            },
            { label: "Comments", value: VisualInspectionViewPageTabs.Comments },
          ]}
        >
          {({ activeTabId: activeTab }) =>
            visualInspection && (
              <>
                {activeTab === VisualInspectionViewPageTabs.Overview && (
                  <OverviewTabContent
                    onUpdate={(newValue) => {
                      newValue
                        ? visualInspectionRequest.replaceData(newValue)
                        : visualInspectionRequest.refetch();
                      onUpdate?.(newValue);
                    }}
                    visualInspection={visualInspection}
                  />
                )}
                {activeTab === VisualInspectionViewPageTabs.Vehicle && (
                  <VehicleTabContent visualInspection={visualInspection} />
                )}
                {activeTab === VisualInspectionViewPageTabs.Contract && (
                  <ContractTabContent visualInspection={visualInspection} />
                )}
                {activeTab === VisualInspectionViewPageTabs.GeneralHistory && (
                  <GeneralHistoryTabContent visualInspection={visualInspection} />
                )}
                {activeTab === VisualInspectionViewPageTabs.Comments && (
                  <CommentsTabContent visualInspection={visualInspection} />
                )}
              </>
            )
          }
        </PageTabsV2>
      </ViewLayout>
    </BaseEntityView>
  );
}
